import { useEffect, useState } from 'react'
import Papa from 'papaparse'

const NISOLO_CSV_URL = 'https://cdn.magicpatterns.com/uploads/3kQQ9Y5yquxtyBvUEKkoAi/nisolo_products_live.csv'

export type NisoloProduct = {
  handle: string
  title: string
  type: string
  status: string
  image: string
  variants: number
  units: number
  price: number
  msrp: number
  sizes: string[]
}

export type NisoloInventory = {
  products: NisoloProduct[]
  totalProducts: number
  liveProducts: number
  totalUnits: number
  liveUnits: number
  totalMsrp: number
  totalRetail: number
}

type CsvRow = Record<string, string>

const IMAGE_PATTERN = /https?:\/\/[^\s"'<>]+?\.(?:jpg|jpeg|png|webp|avif|gif)(?:\?[^\s"'<>]*)?/i

function findImage(row: CsvRow) {
  const preferred = ['Variant Image', 'Image Src', 'Image', 'Images']
  for (const key of preferred) {
    const match = String(row[key] ?? '').match(IMAGE_PATTERN)
    if (match) return match[0]
  }

  for (const value of Object.values(row)) {
    if (!value || value.length > 4000) continue
    if (!value.includes('cdn.shopify.com') && !value.includes('<img')) continue
    const match = value.match(IMAGE_PATTERN)
    if (match) return match[0]
  }

  return ''
}

const toNumber = (value: string | undefined) => {
  const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

function buildInventory(rows: CsvRow[]): NisoloInventory {
  const byHandle = new Map<string, NisoloProduct>()

  rows.forEach((row) => {
    const handle = (row['Handle'] ?? '').trim()
    if (!handle) return

    const qty = toNumber(row['Variant Inventory Qty'])
    const price = toNumber(row['Variant Price'])
    const compareAt = toNumber(row['Variant Compare At Price'])
    const unitMsrp = compareAt > price ? compareAt : price
    const size = (row['Option1 Value'] ?? '').trim()
    const image = findImage(row)

    const existing = byHandle.get(handle)
    if (existing) {
      existing.variants += 1
      existing.units += qty
      existing.price = existing.price || price
      existing.msrp += qty * unitMsrp
      if (size && !existing.sizes.includes(size)) existing.sizes.push(size)
      if (!existing.image && image) existing.image = image
      if (!existing.title && row['Title']) existing.title = row['Title']
      if (!existing.type && row['Type']) existing.type = row['Type']
      if (!existing.status && row['Status']) existing.status = row['Status']
      return
    }

    byHandle.set(handle, {
      handle,
      title: (row['Title'] ?? handle).trim(),
      type: (row['Type'] ?? '').trim(),
      status: (row['Status'] ?? '').trim(),
      image,
      variants: 1,
      units: qty,
      price,
      msrp: qty * unitMsrp,
      sizes: size ? [size] : [],
    })
  })

  const products = Array.from(byHandle.values()).sort((a, b) => b.units - a.units)
  const isLive = (product: NisoloProduct) => product.status.toLocaleLowerCase() === 'active' && product.units > 0

  return {
    products,
    totalProducts: products.length,
    liveProducts: products.filter(isLive).length,
    totalUnits: products.reduce((sum, product) => sum + product.units, 0),
    liveUnits: products.filter(isLive).reduce((sum, product) => sum + product.units, 0),
    totalMsrp: products.reduce((sum, product) => sum + product.msrp, 0),
    totalRetail: products.reduce((sum, product) => sum + product.units * product.price, 0),
  }
}

export function useNisoloProducts(enabled: boolean) {
  const [inventory, setInventory] = useState<NisoloInventory | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled || inventory) return

    let cancelled = false
    setLoading(true)
    setError(null)

    fetch(NISOLO_CSV_URL)
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load the Nisolo product file.')
        return response.text()
      })
      .then(async (text) => {
        if (cancelled) return
        const parsed = Papa.parse<CsvRow>(text, { header: true, skipEmptyLines: true })
        const built = buildInventory(parsed.data)
        setInventory(built)

        const missing = built.products.filter((product) => !product.image).slice(0, 60)
        if (missing.length === 0) return

        const resolved = await Promise.all(missing.map(async (product) => {
          try {
            const response = await fetch(`https://nisolo.com/products/${product.handle}.js`)
            if (!response.ok) return null
            const data = (await response.json()) as { featured_image?: string; images?: string[] }
            const source = data.featured_image ?? data.images?.[0]
            return source ? { handle: product.handle, image: source.startsWith('//') ? `https:${source}` : source } : null
          } catch {
            return null
          }
        }))

        if (cancelled) return
        const lookup = new Map(resolved.filter(Boolean).map((item) => [item!.handle, item!.image]))
        if (lookup.size === 0) return

        setInventory((current) => current ? {
          ...current,
          products: current.products.map((product) => product.image || !lookup.has(product.handle) ? product : { ...product, image: lookup.get(product.handle)! }),
        } : current)
      })
      .catch((caught: Error) => {
        if (!cancelled) setError(caught.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [enabled, inventory])

  return { inventory, loading, error }
}
